import React, { useState, useEffect, useRef } from 'react';
import { DiagnosisDetail } from './DiagnosisDetail';
import { useTranslation } from '../hooks/useTranslation';
import { trackEvent } from '../../utils/Analytics';

export function ResultsDisplay({ results }) {
    const { labReport } = results || {};
    const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const resultsRef = useRef(null);
    const startViewTime = useRef(Date.now());

    useEffect(() => {
        if (labReport) {
            trackEvent('Results', 'view_results', 'Lab Report Viewed', {
                test_name: labReport.testName,
                has_abnormal: labReport.summary.abnormalResults.length > 0
            });

            // Track when component unmounts to measure view duration
            return () => {
                const duration = Math.round((Date.now() - startViewTime.current) / 1000);
                trackEvent('Results', 'results_view_duration', 'Time Spent Viewing Results', duration);
            };
        }
    }, [labReport]);

    // Add intersection observer for diagnosis section
    useEffect(() => {
        if (labReport?.summary?.diffD) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            trackEvent('Results', 'view_diagnosis_section', 'Differential Diagnosis Section Viewed');
                            observer.disconnect(); // Only track first view
                        }
                    });
                },
                { threshold: 0.5 }
            );

            const diagnosisSection = document.querySelector('[data-analytics="diagnosis-section"]');
            if (diagnosisSection) {
                observer.observe(diagnosisSection);
            }

            return () => observer.disconnect();
        }
    }, [labReport]);

    const getStatusStyle = (status) => {
        switch (status.toLowerCase()) {
            case 'high':
                return 'font-bold text-red-700';
            case 'low':
                return 'font-bold text-yellow-700';
            default:
                return 'text-indigo-900';
        }
    };

    const getRowStyle = (status) => {
        switch (status.toLowerCase()) {
            case 'high':
                return 'bg-red-50';
            case 'low':
                return 'bg-yellow-50';
            default:
                return '';
        }
    };

    const getLikelihoodStyle = (likelihood) => {
        switch (likelihood.toLowerCase()) {
            case 'likely':
                return 'bg-indigo-600 text-white border-indigo-700';
            case 'possible':
                return 'bg-indigo-500 text-white border-indigo-600';
            case 'unlikely':
                return 'bg-indigo-400 text-white border-indigo-500';
            case 'rare':
                return 'bg-indigo-300 text-indigo-900 border-indigo-400';
            default:
                return 'bg-indigo-400 text-white border-indigo-500';
        }
    };

    const getSeverityStyle = (severity) => {
        switch (severity.toLowerCase()) {
            case 'severe':
                return {
                    badge: 'bg-indigo-600 text-white border-indigo-700',
                    card: 'bg-red-50 border-red-200'
                };
            case 'moderate':
                return {
                    badge: 'bg-indigo-500 text-white border-indigo-600',
                    card: 'bg-orange-50 border-orange-200'
                };
            case 'mild':
                return {
                    badge: 'bg-indigo-400 text-white border-indigo-500',
                    card: 'bg-indigo-50 border-indigo-200'
                };
            default:
                return {
                    badge: 'bg-indigo-400 text-white border-indigo-500',
                    card: 'bg-indigo-50 border-indigo-200'
                };
        }
    };

    // Function to calculate the position of the value marker on the range bar
    const calculateValuePosition = (value, range) => {
        // Parse the range values
        let min, max;

        if (range.startsWith('<')) {
            // For ranges like "<200"
            max = parseFloat(range.substring(1));
            min = 0; // Assume 0 as the minimum

            // If value is above max, return 100% (far right)
            if (parseFloat(value) > max) {
                return 100;
            }

            // Calculate percentage position
            return (parseFloat(value) / max) * 100;
        } else if (range.includes('-')) {
            // For ranges like "70-99"
            const parts = range.split('-');
            min = parseFloat(parts[0]);
            max = parseFloat(parts[1]);

            // Calculate percentage position
            const percentage = ((parseFloat(value) - min) / (max - min)) * 100;
            return Math.max(0, Math.min(100, percentage)); // Clamp between 0-100%
        }

        // Default fallback
        return 50;
    };

    // Simplified range indicator with subtle accent
    const TufteRangeIndicator = ({ value, range, status }) => {
        const position = calculateValuePosition(value, range);

        // Subtle color scheme with accent
        const normalColor = "bg-gray-50";
        const markerColor = status === 'high' ? "bg-red-600" : status === 'low' ? "bg-amber-600" : "bg-blue-600";

        return (
            <div className={`w-24 h-3 ${normalColor} overflow-hidden flex-shrink-0 relative border border-gray-200 rounded-sm`}>
                {/* Minimal range representation with subtle accent */}
                <div className="absolute inset-0">
                    {/* For ranges with min-max or just max */}
                    {(range.includes('-') || range.startsWith('<')) && (
                        <div className="h-full w-full flex items-center">
                            <div className="h-0.5 w-full bg-gray-300"></div>
                        </div>
                    )}
                </div>

                {/* Value marker with subtle shadow for emphasis */}
                <div
                    className={`absolute top-0 bottom-0 w-1 ${markerColor} shadow-sm`}
                    style={{
                        left: `${position}%`,
                        transform: 'translateX(-50%)'
                    }}
                ></div>

                {/* Minimal range limit markers */}
                {range.includes('-') && (
                    <>
                        <div className="absolute top-0 bottom-0 w-px h-3 bg-gray-400 left-1/5"></div>
                        <div className="absolute top-0 bottom-0 w-px h-3 bg-gray-400 right-1/5"></div>
                    </>
                )}
                {range.startsWith('<') && (
                    <div className="absolute top-0 bottom-0 w-px h-3 bg-gray-400 right-1/5"></div>
                )}
            </div>
        );
    };

    // Show error display if there's an error
    if (error) {
        return <ErrorDisplay error={error} />;
    }

    if (!labReport) {
        return null;
    }

    const renderDiagnosisCard = (diagnosis, index) => {
        const isSerious = diagnosis.isSerious;

        const handleDiagnosisClick = () => {
            trackEvent('Results', 'view_diagnosis_detail', 'Diagnosis Detail Viewed', {
                condition: diagnosis.condition,
                severity: diagnosis.severity,
                likelihood: diagnosis.likelihood
            });
            setSelectedDiagnosis(diagnosis);
        };

        return (
            <div
                key={index}
                onClick={handleDiagnosisClick}
                className={`p-4 rounded-lg border ${getSeverityStyle(diagnosis.severity).card}
                    transition-all duration-150 hover:shadow-md active:shadow-inner
                    hover:border-indigo-300 relative cursor-pointer
                    ${isSerious ? 'border-l-4 border-l-orange-500' : ''}`}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleDiagnosisClick();
                    }
                }}
            >
                <div className="flex items-start justify-between mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <h5 className="text-sm font-medium hover:text-indigo-700 
                            transition-colors flex items-center">
                            <span className="inline-flex items-center">
                                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mr-2">
                                    {index + 1}
                                </span>
                                <span>{diagnosis.condition}</span>
                            </span>
                        </h5>
                        <div className="flex flex-wrap gap-1.5 items-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center
                                ${getLikelihoodStyle(diagnosis.likelihood)}`}>
                                {diagnosis.likelihood}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 relative severity-group
                                ${getSeverityStyle(diagnosis.severity).badge}
                                ${diagnosis.severity === 'severe' ? 'animate-pulse' : ''}`}>
                                <span className="inline-flex items-center">Severity: {diagnosis.severity}</span>
                                <svg className="w-3 h-3 opacity-60 inline-block cursor-help" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 severity-group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                                    Severity based on lab results analysis
                                </div>
                            </span>
                            {diagnosis.likelihood === 'rare' && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center bg-orange-600 text-white">
                                    Rare but possible
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="mt-2 sm:hidden">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDiagnosisClick();
                            }}
                            className="w-full py-1.5 px-3 bg-indigo-500 text-white text-xs font-medium
                                rounded-md hover:bg-indigo-600 active:bg-indigo-700
                                transition-colors duration-150 flex items-center justify-center gap-1.5
                                shadow-sm hover:shadow"
                        >
                            <span>{t('results.differential.moreDetails')}</span>
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                    <div className="hidden sm:block text-indigo-500 hover:text-indigo-600 transition-colors ml-2 flex-shrink-0">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>

                {isSerious && (
                    <div className="mb-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-start space-x-2">
                            <svg className="w-5 h-5 text-orange-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="text-sm">
                                <p className="font-medium text-orange-800">Important Note</p>
                                <p className="text-orange-700">While this is a possibility that needs to be considered,
                                    please discuss with your healthcare provider for proper evaluation.</p>
                                {diagnosis.stats && (
                                    <p className="mt-1 text-orange-600 text-xs">{diagnosis.stats}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {diagnosis.simple_explanation && (
                    <p className="text-sm ml-4 mb-2 text-gray-700 hover:text-gray-900 
                        bg-white bg-opacity-40 p-3 rounded-lg border border-opacity-50">
                        {diagnosis.simple_explanation}
                    </p>
                )}

                {diagnosis.treatment && (
                    <div className="ml-4 p-2 bg-white bg-opacity-50 rounded">
                        <span className="font-medium">{t('results.differential.treatment')}: </span>
                        <span className="text-sm">{diagnosis.treatment}</span>
                    </div>
                )}

                {diagnosis.urgency && (
                    <div className={`mt-2 p-2 rounded-lg text-sm
                        ${diagnosis.urgency === 'urgent' ? 'bg-red-50 text-red-700' :
                            diagnosis.urgency === 'soon' ? 'bg-yellow-50 text-yellow-700' :
                                'bg-green-50 text-green-700'}`}>
                        <span className="font-medium">Follow-up: </span>
                        {diagnosis.urgency === 'urgent' ? 'Urgent attention needed' :
                            diagnosis.urgency === 'soon' ? 'Schedule follow-up soon' :
                                'Routine follow-up'}
                    </div>
                )}

                {/* Smaller, more compact Read More button */}
                <div className="mt-3 ml-8 flex justify-between items-center">
                    <button
                        onClick={() => {
                            trackEvent('Results', 'select_diagnosis', 'Diagnosis Selected', {
                                condition: diagnosis.condition
                            });
                            setSelectedDiagnosis(diagnosis);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-colors"
                    >
                        Read More
                        <svg className="ml-1.5 -mr-0.5 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Smaller severity indicator for severe conditions */}
                    {diagnosis.severity === 'severe' && (
                        <span className="text-xs text-red-600 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Important
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={resultsRef}
            className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-md my-6 relative before:absolute before:top-0 before:left-0 before:w-full before:h-1 before:bg-gradient-to-r before:from-indigo-500 before:to-purple-500"
        >
            <div className="border-b border-gray-200 p-6 bg-gradient-to-r from-gray-50 to-white">
                <h2 className="text-2xl font-bold text-gray-900">{labReport.testName}</h2>
                <p className="text-gray-600 mt-1">{t('results.testInfo.date').replace('{date}', labReport.datePerformed)}</p>
            </div>

            {/* Widescreen layout with subtle color transitions */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-0">
                {/* Left column - Test results and summary with pale blue-gray background */}
                <div className="p-6 space-y-8 bg-blue-50 bg-opacity-20">
                    <div>
                        {/* Section header with soft blue underline and hover effect */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200 transition-colors duration-200 group">
                            <span className="inline-block transition-transform duration-200 group-hover:translate-x-0.5">{t('results.testInfo.testResults')}</span>
                        </h3>
                        <div className="overflow-hidden rounded border border-gray-100 transition-all duration-200 hover:border-blue-200">
                            <table className="w-full">
                                <thead className="bg-blue-50 bg-opacity-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-200">
                                            {t('results.table.parameter')}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 border-b border-gray-200">
                                            {t('results.table.value')}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3 border-b border-gray-200">
                                            {t('results.table.range')}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 border-b border-gray-200">
                                            {t('results.table.status')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {labReport.results.map((result, index) => (
                                        <tr
                                            key={index}
                                            className={`border-b border-gray-100 
                                                ${result.status !== 'normal' ? 'bg-gray-50' : ''} 
                                                hover:bg-blue-50 hover:bg-opacity-40 transition-colors duration-150
                                                cursor-default`}
                                        >
                                            <td className={`px-3 py-3 text-sm ${result.status !== 'normal' ? 'font-semibold' : ''}`}>
                                                {result.parameter}
                                            </td>
                                            <td className={`px-3 py-3 text-sm ${result.status === 'high' ? 'text-red-700' : result.status === 'low' ? 'text-amber-700' : 'text-gray-700'}`}>
                                                <div className="flex items-center">
                                                    <span>{result.value} {result.unit}</span>
                                                    {result.status === 'high' && (
                                                        <svg className="w-3 h-3 ml-1 flex-shrink-0 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    )}
                                                    {result.status === 'low' && (
                                                        <svg className="w-3 h-3 ml-1 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-sm text-gray-600">
                                                <div className="flex items-center">
                                                    <span className="mr-2">{result.referenceRange}</span>
                                                    <TufteRangeIndicator
                                                        value={result.value}
                                                        range={result.referenceRange}
                                                        status={result.status}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-xs">
                                                {result.status !== 'normal' && (
                                                    <span className={`inline-block px-2 py-0.5 rounded-sm transition-colors duration-150 ${result.status === 'high'
                                                        ? 'text-red-800 bg-red-50 hover:bg-red-100'
                                                        : 'text-amber-800 bg-amber-50 hover:bg-amber-100'
                                                        }`}>
                                                        {result.status.toUpperCase()}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="space-y-6 border-t border-gray-200 pt-6">
                        {/* Summary section header with soft green underline and hover effect */}
                        <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b-2 border-green-200 transition-colors duration-200 group">
                            <span className="inline-block transition-transform duration-200 group-hover:translate-x-0.5">{t('results.summary.title')}</span>
                        </h3>

                        {labReport.summary.normalResults.length > 0 && (
                            <div className="bg-green-50 rounded-lg p-4 border border-green-100 shadow-sm">
                                <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('results.summary.normal')}
                                </h4>
                                <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                                    {labReport.summary.normalResults.map((result, index) => (
                                        <li key={index}>{result}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {labReport.summary.abnormalResults.length > 0 && (
                            <div className="border-l-4 border-red-300 pl-4 py-2 bg-red-50 bg-opacity-30 rounded-r transition-all duration-200 hover:border-l-5 hover:bg-opacity-40">
                                {/* Abnormal results header with soft red underline */}
                                <h4 className="text-sm font-bold text-gray-800 mb-2 pb-1 border-b border-red-100">
                                    {t('results.summary.abnormal')}
                                </h4>
                                <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                                    {labReport.summary.abnormalResults.map((result, index) => (
                                        <li key={index} className="text-red-700 transition-colors duration-150 hover:text-red-800">{result}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="border-l-4 border-green-300 pl-4 py-2 bg-green-50 bg-opacity-30 rounded-r transition-all duration-200 hover:border-l-5 hover:bg-opacity-40">
                            {/* Recommendations header with soft green underline */}
                            <h4 className="text-sm font-medium text-gray-800 mb-2 pb-1 border-b border-green-100">
                                {t('results.summary.recommendations')}
                            </h4>
                            <p className="text-sm text-gray-700">{labReport.summary.recommendations}</p>
                        </div>
                    </div>
                </div>

                {/* Right column - Differential diagnosis with pale lavender background */}
                <div className="p-6 space-y-8 lg:border-l lg:border-gray-200 bg-purple-50 bg-opacity-20">
                    {labReport.summary.diffD && (
                        <div data-analytics="diagnosis-section">
                            {/* Differential diagnosis header with soft purple underline and hover effect */}
                            <h3 className="text-lg font-semibold text-gray-900 pb-2 mb-4 border-b-2 border-purple-200 transition-colors duration-200 group">
                                <span className="inline-block transition-transform duration-200 group-hover:translate-x-0.5">{t('results.differential.title')}</span>
                            </h3>
                            <p className="text-sm text-gray-600 mb-2 border-l-4 border-purple-200 pl-4 py-2 bg-purple-50 bg-opacity-30 rounded-r transition-all duration-200 hover:border-l-5 hover:bg-opacity-40">
                                {t('results.differential.explanation')}
                            </p>

                            {/* New: Brief instruction about what users will find */}
                            <p className="text-xs text-purple-700 mb-6 flex items-center">
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Tap any condition below to see detailed information, treatment options, and management recommendations
                            </p>

                            <div className="space-y-4">
                                {labReport.summary.diffD.map((diagnosis, index) => (
                                    <div
                                        key={index}
                                        className="border border-gray-200 rounded-lg p-4 hover:bg-white hover:shadow-md transition-all duration-200 cursor-pointer relative"
                                        onClick={() => {
                                            trackEvent('Results', 'select_diagnosis', 'Diagnosis Selected', {
                                                condition: diagnosis.condition
                                            });
                                            setSelectedDiagnosis(diagnosis);
                                        }}
                                    >
                                        {/* Add a subtle "clickable" indicator in the top right */}
                                        <div className="absolute top-2 right-2 text-purple-300">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                            </svg>
                                        </div>

                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mr-2 transition-colors duration-150 group-hover:bg-purple-200">
                                                    {index + 1}
                                                </span>
                                                <h5 className="text-gray-800 font-medium transition-colors duration-150 hover:text-purple-900">{diagnosis.condition}</h5>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-xs space-x-4 ml-8 mb-2">
                                            <span className={`px-2 py-0.5 rounded-sm transition-colors duration-150 ${diagnosis.likelihood === 'likely'
                                                ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                                : diagnosis.likelihood === 'possible'
                                                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                                }`}>
                                                {diagnosis.likelihood}
                                            </span>
                                            <span>•</span>
                                            <span className={`px-2 py-0.5 rounded-sm transition-colors duration-150 ${diagnosis.severity === 'severe'
                                                ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                                : diagnosis.severity === 'moderate'
                                                    ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                                }`}>
                                                {diagnosis.severity}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-2 ml-8">{diagnosis.simple_explanation || diagnosis.description}</p>

                                        {/* Enhanced Read More button with clearer indication of content */}
                                        <div className="mt-3 ml-8 flex justify-between items-center">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent triggering the parent onClick
                                                    trackEvent('Results', 'select_diagnosis', 'Diagnosis Selected', {
                                                        condition: diagnosis.condition
                                                    });
                                                    setSelectedDiagnosis(diagnosis);
                                                }}
                                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md 
                                                    hover:bg-purple-100 hover:text-purple-800 hover:shadow-sm
                                                    focus:outline-none focus:ring-1 focus:ring-purple-300 focus:shadow-sm
                                                    active:bg-purple-200
                                                    transition-all duration-150"
                                            >
                                                <span className="flex items-center">
                                                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Details & Treatment
                                                </span>
                                                <svg className="ml-1.5 -mr-0.5 w-3.5 h-3.5 transition-transform duration-150 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>

                                            {/* Severity indicator for severe conditions */}
                                            {diagnosis.severity === 'severe' && (
                                                <span className="text-xs text-red-600 flex items-center transition-colors duration-150 hover:text-red-700">
                                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    Important
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedDiagnosis && (
                <DiagnosisDetail
                    diagnosis={selectedDiagnosis}
                    abnormalResults={labReport.summary.abnormalResults}
                    onClose={() => {
                        trackEvent('Results', 'close_diagnosis_detail', 'Diagnosis Detail Closed');
                        setSelectedDiagnosis(null);
                    }}
                />
            )}
        </div>
    );
}